import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import React, { useState, cloneElement, useEffect } from "react";
import { DialogActions, Button } from "@mui/material";
import { useLocation, useNavigate } from "react-router";

type Props = {
    title: string;
    children: React.ReactNode;
    expectedPaths: string[];
};

export default function StatusDialog({
    title,
    children,
    expectedPaths,
}: Props) {
    const location = useLocation();
    const navigate = useNavigate();
    const [isClosing, setIsClosing] = useState(false);

    const open = expectedPaths.some((path) => location.pathname.includes(path));

    // Reset isClosing state when dialog opens
    useEffect(() => {
        if (open && isClosing) {
            setIsClosing(false);
        }
    }, [open, isClosing]);

    const handleClose = () => {
        setIsClosing(true); // Trigger child fade-out instead of immediate navigation

        // Fallback timeout in case animation doesn't complete
        setTimeout(() => {
            onFadeComplete();
        }, 500); // 500ms fallback (longer than the 300ms animation)
    };

    const onFadeComplete = () => {
        // Navigate after fade completes
        const pathParts = location.pathname.split("/");

        // Find the index of any expected path and remove it and everything after it
        for (const expectedPath of expectedPaths) {
            const pathIndex = pathParts.indexOf(expectedPath);
            if (pathIndex !== -1) {
                const parentPath = pathParts.slice(0, pathIndex).join("/");
                navigate(parentPath);
                return;
            }
        }

        // Fallback: just remove the last segment
        const parentPath = pathParts.slice(0, -1).join("/");
        navigate(parentPath);
    };

    // Clone children and pass fade-out props
    const childrenWithProps = cloneElement(
        children as React.ReactElement,
        {
            shouldFadeOut: isClosing,
            onFadeComplete: onFadeComplete,
        } as any,
    );

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{
                sx: {
                    height: "60vh",
                    maxHeight: "60vh",
                },
            }}
        >
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>{childrenWithProps}</DialogContent>
            <DialogActions>
                <Button onClick={handleClose} variant="contained">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}
